import { buildMultiSelectConfirmedAgentMessage } from '@fruitful/llm-service-shared';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Platform, ScrollView, TextInput } from 'react-native';
import {
    ArrowLeftIcon,
    ArrowUpIcon,
    BabyIcon,
    BanknoteIcon,
    BodyMediumRegular,
    BodySmallRegular,
    CarIcon,
    GemIcon,
    getDefaultFontFamily,
    GiftIcon,
    GraduationCapIcon,
    H6Regular,
    HouseIcon,
    HousePlugIcon,
    Icon,
    type IconType,
    PencilIcon,
    PlusIcon,
    Shadows,
    Spinner,
    TicketsPlaneIcon,
    TruckIcon,
    View,
    XStack,
    YStack,
} from 'smoothie';

const OPTION_ICON_MAP: Record<string, IconType> = {
    'home-down-payment': HouseIcon,
    'vacation-travel': TicketsPlaneIcon,
    'new-car': CarIcon,
    'growing-family': BabyIcon,
    'charitable-giving': BanknoteIcon,
    gifts: GiftIcon,
    'home-repairs': HousePlugIcon,
    wedding: GemIcon,
    education: GraduationCapIcon,
    moving: TruckIcon,
};

interface SavingsGoalOptionItem {
    id: string;
    label: string;
    isCustom?: boolean;
    customIndex?: number;
}

interface SavingsGoalsWidgetComponentProps {
    options: SavingsGoalOptionItem[];
    maxSelections: number;
    allowCustomInput: boolean;
    customInputPlaceholder?: string;
    responseKey: string;
    disabled?: boolean;
    isActive?: boolean;
    /**
     * Optional classifier for free-text entries. When provided, a custom entry
     * is checked before it becomes a pill — a non-goal (bills edit, navigation,
     * off-topic) is rejected with a hint instead of added, and a real goal is
     * added using the returned clean short label.
     */
    onClassifyCustomGoal?: (
        inputText: string,
    ) => Promise<{ isGoal: boolean; label: string | null }>;
    onSubmit: (
        displayText: string,
        agentPayload: string,
    ) => void | Promise<void>;
}

export const SavingsGoalsWidget = ({
    options,
    maxSelections,
    allowCustomInput,
    responseKey,
    disabled = false,
    isActive = true,
    onClassifyCustomGoal,
    onSubmit,
}: SavingsGoalsWidgetComponentProps) => {
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [customText, setCustomText] = useState('');
    const [customGoals, setCustomGoals] = useState<string[]>([]);
    const [selectedCustomIndices, setSelectedCustomIndices] = useState<
        Set<number>
    >(new Set());
    const [isAddingCustom, setIsAddingCustom] = useState(false);
    const [isClassifyingCustom, setIsClassifyingCustom] = useState(false);
    const [customError, setCustomError] = useState<string | null>(null);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const customInputRef = useRef<TextInput>(null);

    const isWidgetDisabled = disabled || !isActive || hasSubmitted;
    const totalSelections = selectedIds.size + selectedCustomIndices.size;

    const scrollRef = useRef<ScrollView>(null);
    const dragState = useRef({
        isDown: false,
        startX: 0,
        scrollLeft: 0,
        moved: false,
    });

    useEffect(() => {
        if (Platform.OS !== 'web') return;
        const el =
            (
                scrollRef.current as unknown as {
                    getScrollableNode?: () => HTMLElement;
                }
            )?.getScrollableNode?.() ??
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (scrollRef.current as any)?._nativeRef?.current;
        if (!(el instanceof HTMLElement)) return;

        const onMouseDown = (e: MouseEvent) => {
            dragState.current = {
                isDown: true,
                startX: e.pageX,
                scrollLeft: el.scrollLeft,
                moved: false,
            };
            el.style.cursor = 'grab';
        };
        const onMouseMove = (e: MouseEvent) => {
            if (!dragState.current.isDown) return;
            const dx = e.pageX - dragState.current.startX;
            if (Math.abs(dx) > 3) dragState.current.moved = true;
            el.scrollLeft = dragState.current.scrollLeft - dx;
        };
        const onMouseUp = () => {
            if (dragState.current.isDown) {
                dragState.current.isDown = false;
                el.style.cursor = '';
            }
        };
        const onClick = (e: MouseEvent) => {
            if (dragState.current.moved) {
                e.stopPropagation();
                dragState.current.moved = false;
            }
        };

        el.addEventListener('mousedown', onMouseDown);
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
        el.addEventListener('click', onClick, true);

        return () => {
            el.removeEventListener('mousedown', onMouseDown);
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
            el.removeEventListener('click', onClick, true);
        };
    }, []);

    const allItems = useMemo(() => {
        const customItems: SavingsGoalOptionItem[] = customGoals.map(
            (label, i) => ({
                id: `custom-${i}`,
                label,
                isCustom: true,
                customIndex: i,
            }),
        );
        return [...customItems, ...options];
    }, [customGoals, options]);

    const optionRows = useMemo(() => {
        const mid = Math.ceil(allItems.length / 2);
        return [allItems.slice(0, mid), allItems.slice(mid)];
    }, [allItems]);

    useEffect(() => {
        if (isAddingCustom) {
            setTimeout(() => customInputRef.current?.focus(), 100);
        }
    }, [isAddingCustom]);

    const toggleOption = useCallback(
        (id: string) => {
            if (isWidgetDisabled) return;
            setSelectedIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) {
                    next.delete(id);
                } else {
                    if (next.size + selectedCustomIndices.size >= maxSelections)
                        return prev;
                    next.add(id);
                }
                return next;
            });
        },
        [isWidgetDisabled, maxSelections, selectedCustomIndices.size],
    );

    const hasSelection = totalSelections > 0;

    const stageCustomGoal = useCallback(
        (label: string) => {
            setCustomGoals((prev) => {
                const newIndex = prev.length;
                if (totalSelections < maxSelections) {
                    setSelectedCustomIndices((s) => new Set([...s, newIndex]));
                }
                return [...prev, label];
            });
            setCustomText('');
            setCustomError(null);
            setIsAddingCustom(false);
        },
        [totalSelections, maxSelections],
    );

    const addCustomGoal = useCallback(async () => {
        const trimmed = customText.trim();
        if (trimmed === '' || isClassifyingCustom) return;

        // Without a classifier, keep the original behavior (stage the raw text).
        if (onClassifyCustomGoal == null) {
            stageCustomGoal(trimmed);
            return;
        }

        // Check whether the entry is actually a savings goal before it becomes
        // a pill. A non-goal (bills edit, navigation, off-topic) is rejected
        // with a hint; a real goal is staged using the clean short label.
        setIsClassifyingCustom(true);
        setCustomError(null);
        try {
            const { isGoal, label } = await onClassifyCustomGoal(trimmed);
            if (!isGoal) {
                setCustomError(
                    'That doesn\u2019t look like a savings goal. Try something like \u201cNew car\u201d or \u201cVacation.\u201d',
                );
                return;
            }
            stageCustomGoal(
                label != null && label.trim() !== '' ? label.trim() : trimmed,
            );
        } finally {
            setIsClassifyingCustom(false);
        }
    }, [
        customText,
        isClassifyingCustom,
        onClassifyCustomGoal,
        stageCustomGoal,
    ]);

    const toggleCustomGoal = useCallback(
        (index: number) => {
            if (isWidgetDisabled) return;
            setSelectedCustomIndices((prev) => {
                const next = new Set(prev);
                if (next.has(index)) {
                    next.delete(index);
                } else {
                    if (selectedIds.size + next.size >= maxSelections)
                        return prev;
                    next.add(index);
                }
                return next;
            });
        },
        [isWidgetDisabled, selectedIds.size, maxSelections],
    );

    const handleConfirm = useCallback(() => {
        if (isWidgetDisabled || !hasSelection) return;

        const selectedLabels = options
            .filter((o) => selectedIds.has(o.id))
            .map((o) => o.label);
        const activeCustomGoals = customGoals.filter((_, i) =>
            selectedCustomIndices.has(i),
        );
        const displayParts = [...selectedLabels, ...activeCustomGoals];
        const displayText = displayParts.join(', ');

        const agentPayload = buildMultiSelectConfirmedAgentMessage({
            displayText,
            payload: {
                responseKey,
                selectedOptionIds: [...selectedIds],
                inputText: activeCustomGoals.join(' | '),
            },
        });

        setHasSubmitted(true);
        void onSubmit(displayText, agentPayload);
    }, [
        isWidgetDisabled,
        hasSelection,
        options,
        selectedIds,
        customGoals,
        selectedCustomIndices,
        responseKey,
        onSubmit,
    ]);

    return (
        <YStack
            width="100%"
            paddingHorizontal={16}
            paddingVertical={24}
            paddingBottom={16}
            backgroundColor="$white"
            borderRadius={20}
            gap={16}
            {...Shadows.SMALL}
        >
            {!isAddingCustom && (
                <>
                    <YStack gap={8}>
                        <H6Regular>What are you saving for?</H6Regular>
                        <BodySmallRegular color="$gray1000">
                            Pick up to 3
                        </BodySmallRegular>
                    </YStack>

                    <ScrollView
                        ref={scrollRef}
                        horizontal
                        showsHorizontalScrollIndicator={false}
                        style={{
                            marginHorizontal: -16,
                            ...(Platform.OS === 'web' && {
                                userSelect: 'none' as unknown as undefined,
                            }),
                        }}
                        contentContainerStyle={{
                            paddingHorizontal: 16,
                        }}
                    >
                        <YStack gap={8}>
                            {optionRows.map((row, rowIndex) => (
                                <XStack key={rowIndex} gap={8}>
                                    {row.map((option) => {
                                        const isCustom =
                                            option.isCustom === true;
                                        const isSelected = isCustom
                                            ? selectedCustomIndices.has(
                                                  option.customIndex ?? -1,
                                              )
                                            : selectedIds.has(option.id);
                                        const atLimit =
                                            totalSelections >= maxSelections &&
                                            !isSelected;

                                        return (
                                            <XStack
                                                key={option.id}
                                                paddingHorizontal={16}
                                                paddingVertical={12}
                                                borderRadius={16}
                                                borderWidth={2}
                                                borderColor={
                                                    isSelected
                                                        ? '$black'
                                                        : '$white'
                                                }
                                                backgroundColor="$gray200"
                                                alignItems="center"
                                                gap={6}
                                                cursor={
                                                    isWidgetDisabled || atLimit
                                                        ? 'default'
                                                        : 'pointer'
                                                }
                                                opacity={
                                                    isWidgetDisabled || atLimit
                                                        ? 0.5
                                                        : 1
                                                }
                                                onPress={() => {
                                                    if (isCustom) {
                                                        toggleCustomGoal(
                                                            option.customIndex ??
                                                                -1,
                                                        );
                                                    } else {
                                                        toggleOption(option.id);
                                                    }
                                                }}
                                                hoverStyle={
                                                    !isWidgetDisabled &&
                                                    !atLimit
                                                        ? {
                                                              borderColor:
                                                                  '$accent1000',
                                                          }
                                                        : undefined
                                                }
                                            >
                                                {!isCustom &&
                                                OPTION_ICON_MAP[option.id] !=
                                                    null ? (
                                                    <Icon
                                                        icon={
                                                            OPTION_ICON_MAP[
                                                                option.id
                                                            ] as IconType
                                                        }
                                                        size={18}
                                                        color="$black"
                                                    />
                                                ) : null}
                                                <BodyMediumRegular color="$black">
                                                    {option.label}
                                                </BodyMediumRegular>
                                            </XStack>
                                        );
                                    })}
                                </XStack>
                            ))}
                        </YStack>
                    </ScrollView>
                </>
            )}

            {allowCustomInput && !isAddingCustom && (
                <XStack
                    width="100%"
                    justifyContent="space-between"
                    alignItems="center"
                    gap={12}
                >
                    <XStack
                        flex={1}
                        alignItems="center"
                        gap={12}
                        cursor={isWidgetDisabled ? 'default' : 'pointer'}
                        opacity={isWidgetDisabled ? 0.5 : 1}
                        onPress={() => {
                            if (!isWidgetDisabled) {
                                setIsAddingCustom(true);
                            }
                        }}
                    >
                        <View
                            width={40}
                            height={40}
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Icon icon={PencilIcon} size={16} />
                        </View>
                        <BodyMediumRegular
                            textAlign="left"
                            color="$gray1000"
                            flex={1}
                        >
                            Add a goal
                        </BodyMediumRegular>
                    </XStack>
                    <View
                        width={40}
                        height={40}
                        borderRadius={16}
                        justifyContent="center"
                        alignItems="center"
                        backgroundColor={
                            hasSelection && !isWidgetDisabled
                                ? '$accent1000'
                                : '$gray400'
                        }
                        cursor={
                            hasSelection && !isWidgetDisabled
                                ? 'pointer'
                                : 'default'
                        }
                        onPress={handleConfirm}
                    >
                        <Icon
                            icon={ArrowUpIcon}
                            size={24}
                            color={
                                hasSelection && !isWidgetDisabled
                                    ? '$white'
                                    : '$gray1000'
                            }
                        />
                    </View>
                </XStack>
            )}

            {allowCustomInput && isAddingCustom && (
                <YStack width="100%" gap={12}>
                    <XStack gap={8} alignItems="center">
                        <View
                            width={40}
                            height={40}
                            borderRadius={16}
                            justifyContent="center"
                            alignItems="center"
                            cursor="pointer"
                            onPress={() => {
                                setIsAddingCustom(false);
                            }}
                        >
                            <Icon icon={ArrowLeftIcon} size={24} />
                        </View>
                        <H6Regular>What are you saving for?</H6Regular>
                    </XStack>

                    <XStack width="100%" alignItems="center" gap={12}>
                        <View
                            width={40}
                            height={40}
                            justifyContent="center"
                            alignItems="center"
                        >
                            <Icon icon={PencilIcon} size={16} />
                        </View>
                        <TextInput
                            ref={customInputRef}
                            value={customText}
                            onChangeText={(text) => {
                                setCustomText(text);
                                if (customError != null) setCustomError(null);
                            }}
                            onSubmitEditing={() => {
                                void addCustomGoal();
                            }}
                            editable={!isClassifyingCustom}
                            placeholder="Add a goal"
                            placeholderTextColor="#999"
                            style={{
                                fontFamily: getDefaultFontFamily(),
                                fontSize: 16,
                                fontWeight: '400',
                                lineHeight: 22,
                                flex: 1,
                                paddingVertical: 8,
                            }}
                            aria-label="Custom savings goal"
                        />
                        <View
                            width={40}
                            height={40}
                            borderRadius={16}
                            justifyContent="center"
                            alignItems="center"
                            backgroundColor={
                                customText.trim() !== '' && !isClassifyingCustom
                                    ? '$accent1000'
                                    : '$gray400'
                            }
                            cursor={
                                customText.trim() !== '' && !isClassifyingCustom
                                    ? 'pointer'
                                    : 'default'
                            }
                            onPress={() => {
                                void addCustomGoal();
                            }}
                        >
                            {isClassifyingCustom ? (
                                <Spinner />
                            ) : (
                                <Icon
                                    icon={PlusIcon}
                                    size={24}
                                    color={
                                        customText.trim() !== ''
                                            ? '$white'
                                            : '$gray1000'
                                    }
                                />
                            )}
                        </View>
                    </XStack>
                    {customError != null ? (
                        <BodySmallRegular color="$red1000">
                            {customError}
                        </BodySmallRegular>
                    ) : null}
                </YStack>
            )}
        </YStack>
    );
};
